import PropTypes from "prop-types";
import Dispatcher from "Dispatcher";
import ActiveFormActions from "./ActiveFormActions";
import ActiveSectionActions from "./ActiveSectionActions";
import ContextedReduceStore from "./util/ContextedReduceStore";
import SuperRecordActions from "./SuperRecordActions";
import { translateValue } from "./storeUtils";
import invariant from "invariant";
import NavigationActions from "./NavigationActions";
import NavigatorStore from "./NavigatorStore";
import typeCheckingStore from "data/util/typeCheckingStore";
import { recordIdType } from "data/util/typedefs";
import FormStore from "data/meta/FormStore";
import RecordActions from "data/RecordActions";
import { isConfirmed } from "data/OverStore";
import { ACTION_CONTEXT_KEY } from "data/util/ContextedReduceStore";
import TableStore, { isUnconfirmedVersionChange } from "data/TableStore";

export const FIRST_FIELD = "--first-field--";

function isCurrent(state, action) {
    return state.key === action.key && state.id === action.id;
}

/**
 * I indicate whether the passed data packet describes an element. Element
 * packets are objects containing nullable 'entityKey', 'recordId', and
 * 'domId' keys.
 *
 * @param a a data packet to check
 * @return {boolean} whether the packet describes an element.
 */
export const isElement = a => a != null
    && a.hasOwnProperty("entityKey")
        && a.hasOwnProperty("recordId")
        && a.hasOwnProperty("domId");

/**
 * I indicate whether two element packets describe the same element. Each packet
 * may be null or something isElement returns true when passed.
 *
 * @param a one element packet
 * @param b another element packet
 * @return {boolean} whether the two packets refer to the same element
 */
export const isSameElement = (a, b) => {
    invariant(
        (a == null || isElement(a)) && (b == null || isElement(b)),
        "isSameElement only accepts element packets",
    );
    if (a == null) {
        return b == null;
    } else if (b == null) {
        return false;
    }
    if (a.entityKey !== b.entityKey) {
        return false;
    }
    if (a.recordId !== b.recordId) {
        return false;
    }
    return a.domId === b.domId;
};

/**
 * I indicate whether the passed data packet describes a control. Control
 * packets are objects containing non-null 'entityKey', 'recordId', and
 * 'fieldKey' keys. This matches the format for the active and last blurred
 * controls managed by this store, as well as the FOCUS_ELEMENT action payload.
 *
 * @param a a data packet to check
 * @return {boolean} whether the packet describes a control.
 */
export const isControl = a => a != null
    && !!(a.entityKey
        && a.recordId
        && a.fieldKey);

const processSetFieldState = (ctx, action, initialContext) => {
    if (NavigatorStore.hasChanged()) {
        const navState = NavigatorStore.getState(action[ACTION_CONTEXT_KEY]);
        if (!(action.recordKey !== navState.record.key && action.recordId === navState.record.id)) {
            return initialContext;
        }
    }
    if (ctx.focusMovement) {
        if (isUnconfirmedVersionChange(TableStore, action)) {
            ctx = {
                ...ctx,
                focusMovement: {
                    ...ctx.focusMovement,
                    delta: 0, // always return to the same field
                },
            };
        }
    }
    return ctx;
};

const processStartRenderState = (ctx, action) => {
    if (isCurrent(ctx, action)) {
        // reloading the same form...
        return ctx;
    }
    if (ctx.id === action.id) {
        // just changing versions...
        return {
            ...ctx,
            key: action.key,
            lastBlurredControl: null,
        };
    }
    let fm = ctx.focusMovement;
    if (fm == null) {
        fm = {
            recordId: action.id,
            delta: 0,
            fieldKey: FIRST_FIELD,
        };
    }
    return {
        ...ctx,
        key: action.key,
        id: action.id,
        lastBlurredControl: null,
        activeControl: null,
        focusMovement: fm,
    };
};

const processJumpToSectionState = (ctx, action) => {
    const firstItem = FormStore.getFirstItemInSection(ctx.key, action.section);
    const focusMovement = firstItem == null
        ? null
        : {
            recordId: ctx.id,
            domId: firstItem.id,
            delta: 0,
        };
    return {
        ...ctx,
        focusMovement,
    };
};

const processFocusElementState = (ctx, action) => {
    if (ctx.focusMovement) {
        ctx = {
            ...ctx,
            focusMovement: null,
        };
    }
    if (!isSameElement(ctx.focusedElement, action)) {
        ctx = {
            ...ctx,
            focusedElement: {
                entityKey: action.entityKey,
                recordId: action.recordId,
                domId: action.domId,
                isControl: isControl(action),
            },
        };
    }
    if (isControl(action)) {
        if (!isSameControl(ctx.activeControl, action)) {
            ctx = {
                ...ctx,
                activeControl: {
                    fieldKey: action.fieldKey,
                    entityKey: action.entityKey,
                    recordId: action.recordId,
                    domId: action.domId,
                    keyboardShortcuts: action.keyboardShortcuts,
                },
            };
            // This is pretty gross. But unclear exactly how to do
            // it better, since a) there's no store for it, and b)
            // it's not for every focus action, it's only for focus
            // actions which target a control that isn't already
            // focused. Because "reasons"?
            Backbone.dispatcher.trigger("user:activity");
        }
    } else if (ctx.activeControl) {
        ctx = {
            ...ctx,
            activeControl: null,
        };
    }
    return ctx;
};

const processBlurElementState = (ctx, action) => {
    if (isControl(action) && !isSameControl(
        ctx.lastBlurredControl,
        action,
    )) {
        return {
            ...ctx,
            focusedElement: null,
            lastBlurredControl: {
                fieldKey: action.fieldKey,
                entityKey: action.entityKey,
                recordId: action.recordId,
                domId: action.domId,
            },
            focusMovement: null,
        };
    }
    return ctx;
};

const processMoveFocusState = (ctx, action) => ({
        ...ctx,
        focusedElement: null,
        activeControl: null,
        focusMovement: {
            recordId: action.recordId,
            fieldKey: action.fieldKey,
            domId: action.domId,
            delta: action.delta,
        },
    });

const processPlaceFocusAtStartState = (ctx, action) => ({
        ...ctx,
        focusedElement: null,
        activeControl: null,
        focusMovement: {
            recordId: action.recordId,
            delta: 0,
            fieldKey: FIRST_FIELD,
        },
    });

const processNewChildState = (ctx, action) => ({
        ...ctx,
        lastBlurredControl: null,
        activeControl: null,
        focusMovement: {
            recordId: action.newId,
            delta: 0,
            fieldKey: action.focusFieldKey || FIRST_FIELD,
        },
    });

const processFocusFieldState = (ctx, action) => {
    // since it was in our context, this is initiates a focus change
    if (! action.entityKey) return ctx;
    if (! action.recordId) return ctx;
    let fm;
    if (action.fieldKey && action.fieldKey.hasOwnProperty("fieldKey")) {
        // it's a full triple
        fm = {
            recordId: action.fieldKey.recordId,
            delta: 0,
            fieldKey: action.fieldKey.fieldKey || FIRST_FIELD,
            orNextEnabled: !!action.fieldKey.fieldKey,
        };
    } else {
        fm = {
            recordId: action.recordId,
            delta: 0,
            fieldKey: action.fieldKey || FIRST_FIELD,
            orNextEnabled: !!action.fieldKey,
        };
    }
    return {
        ...ctx,
        lastBlurredControl: null,
        activeControl: null,
        focusMovement: fm,
    };
};

/**
 * I indicate whether two control packets describe the same control. Each packet
 * may be null or something isControl returns true when passed.
 *
 * @param a one control packet
 * @param b another control packet
 * @return {boolean} whether the two packets refer to the same control
 */
export const isSameControl = (a, b) => {
    invariant(
        (a == null || isControl(a)) && (b == null || isControl(b)),
        "isSameControl only accepts control packets",
    );
    if (a == null) {
        return b == null;
    } else if (b == null) {
        return false;
    }
    if (a.entityKey !== b.entityKey) {
        return false;
    }
    if (a.recordId !== b.recordId) {
        return false;
    }
    return a.fieldKey === b.fieldKey;
};

class ActiveFormStore extends ContextedReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialContext() {
        return {
            key: null,
            id: null,
            activeSidebarTab: "definitions",
            focusedElement: null,
            activeControl: null,
            lastBlurredControl: null,
            focusMovement: null,
        };
    }

    reduceContext(ctx, action) {
        this.__dispatcher.waitFor([
            NavigatorStore.getDispatchToken()
        ]);

        // TODO: find out which actions under what
        // conditions need to reset the context

        //if (NavigatorStore.hasChanged()) {
        //    ctx = this.getInitialContext();
        //}

        switch (action.type) {
            case RecordActions.SET_FIELD: {
                return processSetFieldState(ctx, action, this.getInitialContext());
            }

            case ActiveFormActions.START_RENDER: {
                return processStartRenderState(ctx, action);
            }

            case ActiveFormActions.RENDERED:
                if (!isCurrent(ctx, action)) {
                    /*
                    You'd think this wouldn't happen, but because rendering is
                    both async and callback based, there's no way to cancel a
                    requested render if another render is requested. So the
                    first render still ends up happening, into an EntityForm
                    instance which is no longer attached to the view tree.

                    As such, it's not necessarily problematic, but it's pretty
                    damn silly. So log it, but don't raise an exception.
                     */
                    console.warn(`Form '${action.key}#${action.id}' rendered after '${ctx.key}#${ctx.id}' started. Race much?`);
                }
                return ctx;

            case ActiveSectionActions.JUMP_TO_SECTION:
                this.__dispatcher.waitFor([
                    FormStore.getDispatchToken(),
                ]);
                return processJumpToSectionState(ctx, action);

            case ActiveFormActions.FOCUS_ELEMENT: {
                return processFocusElementState(ctx, action);
            }

            case ActiveFormActions.BLUR_ELEMENT: {
                return processBlurElementState(ctx, action);
            }

            case ActiveFormActions.MOVE_FOCUS: {
                return processMoveFocusState(ctx, action);
            }

            case ActiveFormActions.PLACE_FOCUS_AT_START: {
                return processPlaceFocusAtStartState(ctx, action);
            }

            case RecordActions.NEW_CHILD: {
                return processNewChildState(ctx, action);
            }

            case SuperRecordActions.OPEN:
            case NavigationActions.SELECT_RECORD:
            case NavigationActions.FOCUS_FIELD: {
                return processFocusFieldState(ctx, action);
            }

            case SuperRecordActions.NEW:{
                if (! isConfirmed(action)) return ctx;
                return this.getInitialContext();
            }

            case SuperRecordActions.DELETED: {
                return this.getInitialContext();
            }

            case ActiveFormActions.SELECT_SIDEBAR_TAB: {
                return {
                    ...ctx,
                    activeSidebarTab: action.id,
                };
            }

            default:
                return ctx;
        }
    }

    reduceEachContext(ctx, action) {
        switch (action.type) {
            case SuperRecordActions.CREATED:
            case SuperRecordActions.UPDATED:
                // there are four potential IDs to munge
                ctx = translateValue(ctx, ["id"], action.data.newIds);
                ctx = translateValue(
                    ctx,
                    ["focusedElement", "recordId"],
                    action.data.newIds,
                );
                ctx = translateValue(
                    ctx,
                    ["activeControl", "recordId"],
                    action.data.newIds,
                );
                // clear the lastBlurredControl. For refocus reasons this will
                // let us know that the activeControl and lastBlurredControl
                // are different and we need to kick off a refocus.
                ctx.lastBlurredControl = null;
                return ctx;

            case SuperRecordActions.DELETED:
                if (ctx.id === action.id) {
                    return this.getInitialContext();
                }
                return ctx;

            case RecordActions.DELETE_RECORD:
                if (! isConfirmed(action)) return ctx;
                if (ctx.id === action.recordId) {
                    return this.getInitialContext();
                }
                return ctx;

            case RecordActions.DELETE_CHILD:
                if (! isConfirmed(action)) return ctx;
                // down the tree
                if (ctx.id === action.childId) {
                    // eventually this should probably set what was navigated to
                    // (i.e., waitFor NavStore, and as it how to initialize),
                    // but that points to _this_ store doing bad things by
                    // duplicating data...
                    return this.getInitialContext();
                }
                return ctx;

            default:
                return ctx;
        }
    }

    getActiveSidebarTab(context) {
        return this.getState(context).activeSidebarTab;
    }

    getActiveControl(context) {
        return this.getState(context).activeControl;
    }

    getFocusedElement(context) {
        return this.getState(context).focusedElement;
    }

    getLastBlurredControl(context) {
        return this.getState(context).lastBlurredControl;
    }

    getFocusMovement(context) {
        return this.getState(context).focusMovement;
    }

}

ActiveFormStore.contextTypes = {
    key: PropTypes.string,
    id: recordIdType,
    activeSidebarTab: PropTypes.string.isRequired,
    focusedElement: PropTypes.shape({
        entityKey: PropTypes.string,
        recordId: recordIdType,
        domId: PropTypes.string.isRequired,
        isControl: PropTypes.bool.isRequired,
    }),
    activeControl: PropTypes.shape({
        fieldKey: PropTypes.string.isRequired,
        entityKey: PropTypes.string.isRequired,
        recordId: recordIdType.isRequired,
        domId: PropTypes.string,
        keyboardShortcuts: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
            })
        ),
    }),
    lastBlurredControl: PropTypes.shape({
        fieldKey: PropTypes.string.isRequired,
        entityKey: PropTypes.string.isRequired,
        recordId: recordIdType.isRequired,
        domId: PropTypes.string.isRequired,
    }),
    focusMovement: PropTypes.exact({
        // no entityKey to allow movement across version changes
        // recordId is need for motion w/in a subform (e.g., grid)
        recordId: recordIdType.isRequired,
        domId: PropTypes.string,
        fieldKey: PropTypes.string,
        // how to move from the specified anchor
        delta: PropTypes.number.isRequired,
        orNextEnabled: PropTypes.bool,
    }),
};

export default typeCheckingStore(new ActiveFormStore());



// WEBPACK FOOTER //
// ./app/data/ActiveFormStore.js